import type { GraphSidePanelContentId } from '@ipfabric/utils';
import type {
  IntentVerificationTabId,
  NetworkViewerTabId,
  PathLookupDataInputTabId,
  PathLookupTabId,
  PathLookupVisualizationSetupTabId,
  TopologyLayoutSettingsTabId,
} from 'components/Graph/GraphSidePanelData';
import type { GraphCommandFactory } from 'components/Graph/model/GraphCommandContext';
import { produce } from 'immer';
import { performLeavingAction } from './leavingDialog/performLeavingAction';

export interface ShowSidePanelCommand {
  contentId?: GraphSidePanelContentId;
  intentVerificationTabId?: IntentVerificationTabId;
  networkViewerTabId?: NetworkViewerTabId;
  pathLookupDataInputTabId?: PathLookupDataInputTabId;
  pathLookupTabId?: PathLookupTabId;
  pathLookupVisualizationSetupTabId?: PathLookupVisualizationSetupTabId;
  topologyLayoutSettingsTabId?: TopologyLayoutSettingsTabId;
}

export const showSidePanel: GraphCommandFactory<ShowSidePanelCommand> =
  (cmdContext, dispatch) => (command) => {
    const nextSidePanelState = produce(
      cmdContext.getState().sidePanel,
      // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: complex decision
      (draft) => {
        draft.open = true;
        // the condition should go from inner tabs to outer tabs;
        // this allows consistent behavior when outer tab does not match inner tab,
        // e.g. contentId: networkViewer, pathLookupDataInputTabId: ...
        // that should (keep) open network viewer but change path lookup tabs behind the scenes.
        if (command.topologyLayoutSettingsTabId) {
          draft.topologyLayoutSettingsTabId =
            command.topologyLayoutSettingsTabId;
          draft.networkViewerTabId = 'visualizationSetup';
          draft.contentId = 'networkViewer';
        }
        if (command.intentVerificationTabId) {
          draft.intentVerificationTabId = command.intentVerificationTabId;
          draft.contentId = 'intentVerification';
        }
        if (command.networkViewerTabId) {
          draft.networkViewerTabId = command.networkViewerTabId;
          draft.contentId = 'networkViewer';
        }
        if (command.pathLookupDataInputTabId) {
          draft.pathLookupDataInputTabId = command.pathLookupDataInputTabId;
          draft.pathLookupTabId = 'dataInput';
          draft.contentId = 'pathlookup';
        }
        if (command.pathLookupVisualizationSetupTabId) {
          draft.pathLookupVisualizationSetupTabId =
            command.pathLookupVisualizationSetupTabId;
          draft.pathLookupTabId = 'visualizationSetup';
          draft.contentId = 'pathlookup';
        }
        if (command.pathLookupTabId) {
          draft.pathLookupTabId = command.pathLookupTabId;
          draft.contentId = 'pathlookup';
        }
        if (command.contentId) {
          draft.contentId = command.contentId;
        }
      },
    );

    dispatch(performLeavingAction, {
      callback: () => {
        cmdContext.updateState((draft) => {
          draft.sidePanel = nextSidePanelState;
        });

        if (
          nextSidePanelState.networkViewerTabId !== 'visualizationSetup' ||
          nextSidePanelState.contentId !== 'networkViewer'
        ) {
          cmdContext.updateState((draft) => {
            draft.layoutSettings.presetEditMode = false;
          });
        }
      },
      nextSidePanelState,
    });
  };
